import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { InvestmentsAPI, InvestmentPayload } from '../services/investments.ts';
import {
  formatCurrency,
  applyCurrencyMask,
  parseCurrency,
  getCurrencyLocale,
} from '../utils/currency.ts';
import FormPage from '../components/ui/FormPage.tsx';
import MoreOptions from '../components/ui/MoreOptions.tsx';

const CreateInvestment: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<InvestmentPayload>({
    description: '',
    category: 'fixed_income',
    investedValue: 0,
    currentValue: undefined,
    status: 'active',
    currency: 'BRL',
    ticker: '',
    quantity: null,
  });
  const [investedValueDisplay, setInvestedValueDisplay] = useState('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation(async () => InvestmentsAPI.create(form), {
    onSuccess: created => {
      queryClient.invalidateQueries(['investments']);
      queryClient.invalidateQueries(['investments-portfolio']);
      navigate(`/investments/${created.id}`);
    },
    onError: () => setErrors(prev => ({ ...prev, submit: 'Erro ao criar investimento.' })),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }) as any);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyChange = (field: 'investedValue' | 'currentValue', rawValue: string) => {
    const locale = getCurrencyLocale(form.currency || 'BRL');
    const masked = applyCurrencyMask(rawValue, locale);
    const numeric = parseCurrency(masked, locale);
    if (field === 'investedValue') setInvestedValueDisplay(masked);
    else setCurrentValueDisplay(masked);
    setForm(prev => ({ ...prev, [field]: numeric }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!form.description?.trim()) nextErrors.description = 'Nome / descrição é obrigatório';
    if (!form.category) nextErrors.category = 'Categoria é obrigatória';
    if (!form.status) nextErrors.status = 'Status é obrigatório';
    if (!form.investedValue || form.investedValue <= 0) {
      nextErrors.investedValue = 'Valor investido deve ser maior que zero';
    }
    if (form.currentValue !== undefined && form.currentValue < 0) {
      nextErrors.currentValue = 'Valor de mercado não pode ser negativo';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Market value defaults to invested; backend appends 1º ponto no histórico de preços
    createMutation.mutate();
  };

  return (
    <FormPage
      backTo="/investments"
      backLabel="Voltar aos investimentos"
      breadcrumb="Patrimônio / Investimentos / Novo"
      title="Novo investimento"
      subtitle="Cadastro completo do ativo"
      hint={
        <>
          Campos essenciais primeiro. Prefira <strong>Importar</strong> para B3/XP/Clear/BTG quando
          houver arquivo. Após salvar, o sistema cria o 1º ponto no{' '}
          <strong>histórico de preços</strong>.
        </>
      }
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/investments')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="create-investment-form"
            className="btn btn-primary"
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Salvando…' : 'Salvar investimento'}
          </button>
        </>
      }
    >
      <form id="create-investment-form" noValidate onSubmit={onSubmit}>
        {errors.submit && (
          <div className="field-error" style={{ marginBottom: 12 }}>
            {errors.submit}
          </div>
        )}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="description">Nome / descrição *</label>
            <input
              id="description"
              name="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={form.description}
              onChange={handleChange}
              placeholder="Ex.: CDB Banco Inter 115% CDI"
            />
            {errors.description && <p className="field-error">{errors.description}</p>}
          </div>
          <div className="field">
            <label htmlFor="category">Categoria *</label>
            <select
              id="category"
              name="category"
              className="select"
              value={form.category}
              onChange={handleChange}
            >
              <option value="fixed_income">Renda fixa</option>
              <option value="stocks">Renda variável / Ações</option>
              <option value="funds">Fundos</option>
              <option value="crypto">Cripto</option>
              <option value="offshore">Offshore</option>
              <option value="other">Outros</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              className="select"
              value={form.status}
              onChange={handleChange}
            >
              <option value="active">Ativo</option>
              <option value="sold">Encerrado</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="investedValue">Custo / aplicação (R$) *</label>
            <input
              id="investedValue"
              className={`input${errors.investedValue ? ' error' : ''}`}
              value={investedValueDisplay}
              onChange={e => handleCurrencyChange('investedValue', e.target.value)}
              placeholder={formatCurrency(
                0,
                form.currency || 'BRL',
                getCurrencyLocale(form.currency || 'BRL')
              )}
              inputMode="decimal"
            />
            {errors.investedValue && <p className="field-error">{errors.investedValue}</p>}
          </div>
          <div className="field">
            <label htmlFor="currentValue">Valor de mercado (R$)</label>
            <input
              id="currentValue"
              className={`input${errors.currentValue ? ' error' : ''}`}
              value={currentValueDisplay}
              onChange={e => handleCurrencyChange('currentValue', e.target.value)}
              placeholder="Opcional — usa o custo se vazio"
              inputMode="decimal"
            />
            {errors.currentValue && <p className="field-error">{errors.currentValue}</p>}
          </div>
          <div className="field">
            <label htmlFor="currency">Moeda</label>
            <select
              id="currency"
              name="currency"
              className="select"
              value={form.currency || 'BRL'}
              onChange={handleChange}
            >
              <option value="BRL">BRL</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ticker">Ticker / código</label>
              <input
                id="ticker"
                name="ticker"
                className="input"
                value={form.ticker || ''}
                onChange={handleChange}
                placeholder="opcional"
              />
            </div>
            <div className="field">
              <label htmlFor="quantity">Quantidade</label>
              <input
                id="quantity"
                className="input"
                type="number"
                step="any"
                value={form.quantity ?? ''}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    quantity: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                placeholder="opcional"
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default CreateInvestment;
